import { useCallback } from 'react'
import useAuthSession from '../hooks/useAuthSession.js'
import useFlashcards from '../hooks/useFlashcards.js'
import AppShell from './AppShell.jsx'
import ImportForm from './ImportForm.jsx'
import FlashcardSection from './FlashcardSection.jsx'

/**
 * AppPage component is the main protected application view.
 * Combines import functionality and flashcard display.
 *
 * @returns {React.ReactElement} App page
 */
export default function AppPage() {
  const { session, signOut } = useAuthSession()
  const {
    cards,
    isInitialLoading,
    isRefetching,
    dataError,
    mutatingIds,
    isDeletingAll,
    insert,
    updateCard,
    remove,
    deleteAllCards,
    refetch,
  } = useFlashcards()

  // Handle import from ImportForm
  const handleImport = useCallback(
    async (flashcards) => {
      return await insert(flashcards)
    },
    [insert]
  )

  // Handle update from FlashcardSection
  const handleUpdate = useCallback(
    async (id, updates) => {
      return await updateCard(id, updates)
    },
    [updateCard]
  )

  // Handle delete from FlashcardSection
  const handleDelete = useCallback(
    async (id) => {
      return await remove(id)
    },
    [remove]
  )

  // Handle delete all from FlashcardSection
  const handleDeleteAll = useCallback(async () => {
    return await deleteAllCards()
  }, [deleteAllCards])

  // Handle retry from ErrorAlert
  const handleRetry = useCallback(() => {
    void refetch()
  }, [refetch])

  // Handle sign out
  const handleSignOut = useCallback(async () => {
    await signOut()
    // Clear any localStorage data
    if (session?.user?.id) {
      try {
        localStorage.removeItem(`flashcards-import-${session.user.id}`)
      } catch (error) {
        console.warn('Failed to clear localStorage:', error)
      }
    }
  }, [signOut, session])

  return (
    <AppShell
      flashcardCount={cards.length}
      onSignOut={handleSignOut}
      showGlobalLoader={false}
    >
      {/* Import section */}
      <ImportForm
        onImport={handleImport}
        userId={session?.user?.id || null}
        currentFlashcardCount={cards.length}
      />

      {/* Flashcards section */}
      <FlashcardSection
        cards={cards}
        isInitialLoading={isInitialLoading}
        isRefetching={isRefetching}
        dataError={dataError}
        mutatingIds={mutatingIds}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        onDeleteAll={handleDeleteAll}
        isDeletingAll={isDeletingAll}
        onRetry={handleRetry}
      />
    </AppShell>
  )
}
